import {
	Drawer,
	PopoverContent,
	Progress,
	StarIcon,
	XIcon,
} from "@au-schein-de/au-ui-library";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";

export type RatingBreakdownEntry = {
	star: number;
	count: number;
	percentage: number;
};

type RatingBreakdownPopoverContentProps = {
	averageRating: number;
	totalCount: number;
	breakdown: ReadonlyArray<RatingBreakdownEntry>;
	onClose: () => void;
	align?: "start" | "center" | "end";
};

type RatingBreakdownVariant = "desktop" | "mobile";

function RatingBreakdownContent({
	averageRating,
	totalCount,
	breakdown,
	onClose,
	variant = "desktop",
}: {
	averageRating: number;
	totalCount: number;
	breakdown: ReadonlyArray<RatingBreakdownEntry>;
	onClose: () => void;
	variant?: RatingBreakdownVariant;
}) {
	const { t } = useTranslation();
	const isMobile = variant === "mobile";

	return (
		<div className={`flex flex-col ${isMobile ? "gap-6" : "gap-4"}`}>
			<div className="flex items-start justify-between gap-3">
				<div className="flex flex-col">
					<div className="flex items-baseline gap-1">
						<span className="text-h3 text-gray-700">
							{averageRating.toFixed(1)}
						</span>
						<span className="text-body-sm text-gray-500">/ 5</span>
					</div>
					<div className="flex gap-1 text-body-sm text-gray-500">
						<span className="text-body-sm-emphasis text-gray-700">
							{totalCount}
						</span>
						{t("ratingBreakdownTotalRatings", {
							count: Number(totalCount) || 0,
						})}
					</div>
				</div>
				<button
					type="button"
					onClick={onClose}
					className={`flex items-center justify-center rounded-full p-3 size-10 shrink-0 cursor-pointer outline-none border-0 ${isMobile ? "bg-white" : "bg-gray-100"}`}
					aria-label={t("close")}
				>
					<XIcon size={6} />
				</button>
			</div>

			<div className={`flex flex-col ${isMobile ? "gap-2" : "gap-1"}`}>
				{breakdown.map(({ star, percentage }) => (
					<div
						key={star}
						className={`flex items-center ${isMobile ? "gap-3" : "gap-2"}`}
					>
						<span className="text-body-sm text-gray-700 w-6 flex items-center gap-1 shrink-0">
							{star}
							<StarIcon.Raw className="w-2.5 h-2.5 text-orange-700" />
						</span>
						<Progress variant="rating" value={percentage} className="flex-1" />
						<span className="text-body-sm text-gray-700 w-[37px] text-right shrink-0">
							{percentage}%
						</span>
					</div>
				))}
			</div>
		</div>
	);
}

export function RatingBreakdownPopoverPanel({
	averageRating,
	totalCount,
	breakdown,
	onClose,
	align = "start",
}: RatingBreakdownPopoverContentProps) {
	return (
		<PopoverContent
			className="w-[296px] p-4 bg-white rounded-2xl drop-shadow-[0px_10px_10px_rgba(33,42,54,0.16)] border-0 outline-none"
			align={align}
			sideOffset={8}
		>
			<RatingBreakdownContent
				averageRating={averageRating}
				totalCount={totalCount}
				breakdown={breakdown}
				onClose={onClose}
				variant="desktop"
			/>
		</PopoverContent>
	);
}

type RatingBreakdownDrawerProps = {
	averageRating: number;
	totalCount: number;
	breakdown: ReadonlyArray<RatingBreakdownEntry>;
	isOpen: boolean;
	onOpenChange: (open: boolean) => void;
	children: ReactNode;
};

export function RatingBreakdownDrawer({
	averageRating,
	totalCount,
	breakdown,
	isOpen,
	onOpenChange,
	children,
}: RatingBreakdownDrawerProps) {
	const { t } = useTranslation();
	const handleClose = () => onOpenChange(false);

	return (
		<Drawer.Root open={isOpen} onOpenChange={onOpenChange} modal>
			<Drawer.Trigger asChild>{children}</Drawer.Trigger>
			<Drawer.Content
				className="rounded-t-3xl bg-gray-100 outline-none border-0"
				aria-describedby={undefined}
			>
				<Drawer.Title className="sr-only">
					{t("ratingBreakdownTitle")}
				</Drawer.Title>
				<div className="px-4 pb-6 pt-4">
					<RatingBreakdownContent
						averageRating={averageRating}
						totalCount={totalCount}
						breakdown={breakdown}
						onClose={handleClose}
						variant="mobile"
					/>
				</div>
			</Drawer.Content>
		</Drawer.Root>
	);
}
